import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";

export default function PlayerCard({ player, type, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const stats = player.stats || {};

  const hitterMainStats = [
    { label: "AVG", value: stats.avg || ".000" },
    { label: "HR", value: stats.home_runs || 0 },
    { label: "RBI", value: stats.rbi || 0 },
    { label: "OPS", value: stats.ops || ".000" }
  ];

  const hitterAdvancedStats = [
    { label: "OBP", value: stats.obp || ".000" },
    { label: "SLG", value: stats.slg || ".000" },
    { label: "fWAR", value: stats.fwar ?? "-" },
    { label: "wRC+", value: stats.wrc_plus ?? "-" },
    { label: "BB", value: stats.walks || 0 },
    { label: "K", value: stats.strikeouts || 0 },
    { label: "BABIP", value: stats.babip || ".000" },
    { label: "SB", value: stats.stolen_bases || 0 }
  ];

  const pitcherMainStats = [
    { label: "ERA", value: stats.era || "0.00" },
    { label: "W-L", value: `${stats.wins || 0}-${stats.losses || 0}` },
    { label: "IP", value: stats.innings_pitched || "0" },
    { label: "K", value: stats.strikeouts || 0 }
  ];

  const pitcherAdvancedStats = [
    { label: "FIP", value: stats.fip ?? "0.00" },
    { label: "fWAR", value: stats.fwar ?? "-" },
    { label: "WHIP", value: stats.whip || "0.00" },
    { label: "K/9", value: stats.k_per_9 || "0.00" },
    { label: "BB/9", value: stats.bb_per_9 || "0.00" },
    { label: "BABIP", value: stats.babip || ".000" },
    { label: "BAA", value: stats.avg_against || ".000" },
    { label: "SV", value: stats.saves || 0 }
  ];

  const mainStats = type === "hitter" ? hitterMainStats : pitcherMainStats;
  const advancedStats = type === "hitter" ? hitterAdvancedStats : pitcherAdvancedStats;

  return (
    <motion.div
      className={`bg-astros-surface border border-astros-border player-card ${compact ? "p-3" : "p-4"}`}
      data-testid={`player-card-${player.id}`}
      whileHover={{ borderColor: "#EB6E1F" }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0 cursor-pointer"
          onClick={() => navigate(`/player/${player.id}`)}
          data-testid={`player-photo-${player.id}`}
        >
          <img
            src={player.photo_url || "https://images.unsplash.com/photo-1770228783704-32823e759c03?w=80&h=80&fit=crop"}
            alt={player.name}
            className={`${compact ? "w-12 h-12" : "w-16 h-16"} object-cover bg-astros-navy hover:opacity-80 transition-opacity`}
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1770228783704-32823e759c03?w=80&h=80&fit=crop"; }}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div
              className="cursor-pointer hover:text-astros-orange transition-colors"
              onClick={() => navigate(`/player/${player.id}`)}
            >
              <h4 className={`font-ibm-plex font-semibold text-white truncate ${compact ? "text-sm" : "text-base"}`}>
                {player.name}
              </h4>
              <div className="flex items-center gap-2 text-xs text-astros-muted">
                <span className="text-astros-orange font-bold">#{player.jersey_number || "00"}</span>
                <span>{player.position}</span>
                {stats.fwar != null && !compact && (
                  <span className={`font-bold ${stats.fwar >= 0 ? "text-green-400" : "text-red-400"}`}>
                    fWAR: {stats.fwar}
                  </span>
                )}
              </div>
            </div>

            {!compact && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-astros-muted hover:text-astros-orange transition-colors"
                data-testid={`expand-${player.id}`}
              >
                {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            )}
          </div>

          <div className={`grid ${compact ? "grid-cols-4 gap-2 mt-2" : "grid-cols-4 gap-3 mt-3"}`}>
            {mainStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className={`font-ibm-plex font-bold text-white ${compact ? "text-sm" : "text-lg"}`}>
                  {stat.value}
                </p>
                <p className="text-xs text-astros-muted uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>

          {!compact && expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mt-4 pt-4 border-t border-astros-border"
            >
              <p className="text-xs text-astros-muted uppercase tracking-widest mb-3 font-bold">Advanced Stats</p>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
                {advancedStats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="font-ibm-plex font-bold text-white text-sm">{stat.value}</p>
                    <p className="text-xs text-astros-muted uppercase tracking-wider">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
